/**
 * ч.945: швидке створення КОНТРАГЕНТА з форми документа (каса, і далі —
 * решта документів). На імпорті виписки живе свій варіант, що бере реквізити
 * з рядка виписки; тут — мінімум полів, які бухгалтер вводить руками.
 */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { contractors as contractorsApi, type ContractorCreate, type ContractorType } from '@/api/catalogs';
import ContractorCodeHint from './ContractorCodeHint';
import { invalidateRefLists } from '@/shared/utils/refLists';

const TYPE_LABELS: Record<ContractorType, string> = {
  LEGAL: 'Юридична особа',
  INDIVIDUAL_FOP: 'ФОП',
  INDIVIDUAL: 'Фізична особа',
  SEPARATE_DIVISION: 'Відокремлений підрозділ',
};

/** ч.1096: реквізити з розпізнаного документа — передзаповнення форми. */
export interface QuickContractorInitial {
  name?: string | null;
  full_name?: string | null;
  edrpou?: string | null;
  /** ІПН платника ПДВ (12 цифр) або РНОКПП ФОП (10 цифр). */
  ipn?: string | null;
  address?: string | null;
}

/** Що передзаповнити з реквізитів документа: тип за кодом, ПДВ за ІПН. */
export function initialFromDocument(init: QuickContractorInitial | undefined) {
  const edrpou = (init?.edrpou ?? '').replace(/\D/g, '');
  const ipn = (init?.ipn ?? '').replace(/\D/g, '');
  const isFop = !edrpou && ipn.length === 10;
  return {
    name: (init?.name ?? '').trim(),
    fullName: (init?.full_name ?? '').trim() || null,
    ctype: (isFop ? 'INDIVIDUAL_FOP' : 'LEGAL') as ContractorType,
    code: edrpou || (isFop ? ipn : ''),
    vatNumber: ipn.length === 12 ? ipn : null,
    address: (init?.address ?? '').trim() || null,
  };
}

export default function QuickContractorModal({ direction, onClose, onCreated, initial }: {
  /** IN — гроші до нас (за замовч. покупець), OUT — від нас (постачальник). */
  direction: 'IN' | 'OUT';
  onClose: () => void;
  onCreated: (id: string) => void;
  initial?: QuickContractorInitial;
}) {
  const qc = useQueryClient();
  const pre = initialFromDocument(initial);
  const [name, setName] = useState(pre.name);
  const [ctype, setCtype] = useState<ContractorType>(pre.ctype);
  const [code, setCode] = useState(pre.code);
  const [isBuyer, setIsBuyer] = useState(direction === 'IN');
  const [isSupplier, setIsSupplier] = useState(direction === 'OUT');
  const [err, setErr] = useState<string | null>(null);
  const [similar, setSimilar] = useState(0);   // ч.1237: скільки схожих уже є в довіднику

  const create = useMutation({
    mutationFn: () => {
      const isLegal = ctype === 'LEGAL' || ctype === 'SEPARATE_DIVISION';
      const payload: ContractorCreate = {
        code: null, name: name.trim(), full_name: pre.fullName, contractor_type: ctype,
        edrpou: isLegal ? (code.trim() || null) : null,
        inn: !isLegal ? (code.trim() || null) : null,
        is_vat_payer: !!pre.vatNumber, vat_number: pre.vatNumber, tax_system: null,
        legal_address: pre.address, actual_address: null, phone: null, email: null,
        is_buyer: isBuyer, is_supplier: isSupplier, is_other: !isBuyer && !isSupplier,
        is_non_resident: false, group_id: null, kved_code: null,
      };
      return contractorsApi.create(payload);
    },
    // ч.1267: створений контрагент одразу є в усіх списках (сторінки читають різні ключі)
    onSuccess: (c) => { invalidateRefLists(qc, 'contractors'); onCreated(c.id); },
    onError: (e: unknown) => {
      const d = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
      setErr(typeof d === 'string' ? d
        : Array.isArray(d) ? d.map((x: { msg?: string }) => x.msg).join('; ')
        : 'Не вдалося створити контрагента');
    },
  });

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'var(--overlay)', zIndex: 1200,
      display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div className="card" style={{ width: 460, maxWidth: '95vw', padding: '1rem' }}
        onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: '0 0 0.6rem' }}>Новий контрагент</h3>
        {initial && (
          <div style={{ fontSize: '0.8rem', color: 'var(--badge-amber-fg)', marginBottom: '0.5rem' }}>
            Реквізити взято з документа — перевірте назву і код перед створенням.
            {pre.vatNumber ? ` ІПН ${pre.vatNumber} → платник ПДВ.` : ''}
            {pre.address ? ' Адресу збережемо у картці.' : ''}
          </div>
        )}
        <div className="field"><label>Найменування *</label>
          <input value={name} onChange={(e) => setName(e.target.value)} autoFocus /></div>
        {/* ч.1095: такий код уже є — обрати наявного замість створення дубля. */}
        <ContractorCodeHint code={code} name={name} onMatches={setSimilar}
          onPick={(m) => onCreated(m.id)}
          onOpen={(m) => window.open(`/contractors/${m.id}`, '_blank', 'noopener')} />
        <div className="doc-grid">
          <div className="field" style={{ gridColumn: 'span 7' }}><label>Тип</label>
            <select value={ctype} onChange={(e) => setCtype(e.target.value as ContractorType)}>
              {(Object.keys(TYPE_LABELS) as ContractorType[]).map((k) => (
                <option key={k} value={k}>{TYPE_LABELS[k]}</option>
              ))}
            </select></div>
          <div className="field" style={{ gridColumn: 'span 5' }}>
            <label>{ctype === 'LEGAL' || ctype === 'SEPARATE_DIVISION' ? 'ЄДРПОУ' : 'РНОКПП'}</label>
            <input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              inputMode="numeric" maxLength={10} /></div>
        </div>
        <div style={{ display: 'flex', gap: '1rem', margin: '0.2rem 0 0.6rem' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
            <input type="checkbox" checked={isBuyer} style={{ width: 'auto' }}
              onChange={(e) => setIsBuyer(e.target.checked)} /> покупець
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
            <input type="checkbox" checked={isSupplier} style={{ width: 'auto' }}
              onChange={(e) => setIsSupplier(e.target.checked)} /> постачальник
          </label>
        </div>
        <div style={{ fontSize: '0.8rem', color: 'var(--muted)', marginBottom: '0.5rem' }}>
          Решта реквізитів (адреса, рахунки, ПДВ) — у картці контрагента.
        </div>
        {err && <div className="error">{err}</div>}
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
          <button type="button" onClick={onClose}>скасувати</button>
          <button type="button" className="primary"
            disabled={create.isPending || name.trim().length < 2}
            onClick={() => { setErr(null); create.mutate(); }}>
            {similar > 0 ? 'усе одно створити' : 'створити'}</button>
        </div>
      </div>
    </div>
  );
}
